import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { StudioBuilder } from "@/components/studio/StudioBuilder";
import { StudioPreview } from "@/components/studio/StudioPreview";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { IMAGES, PICKLE_BASES, REGIONAL_STYLES, TEXTURES, STUDIO_INGREDIENTS, JAR_SIZES } from "@/lib/constants";
import { ArrowLeft, ShoppingBag, Check, RotateCcw, User, Lock, Eye, X, Flame, Droplets, Beef } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STEPS = ["Base", "Style", "Taste", "Texture", "Ingredients", "Profile", "Size", "Personalize"];
const STEP_ITEMS = [
  { id: "base", label: "Base" },
  { id: "style", label: "Style" },
  { id: "taste", label: "Taste" },
  { id: "texture", label: "Texture" },
  { id: "ingredients", label: "Ingredients" },
  { id: "profile", label: "Profile" },
  { id: "jar-size", label: "Size" },
  { id: "personalize", label: "Finish" },
];
const LAST_STEP_INDEX = STEP_ITEMS.length - 1;

const STUDIO_CATEGORIES = [
  { id: "pickles", label: "Pickles", active: true },
  { id: "podi", label: "Podi", active: false },
  { id: "sweets", label: "Sweets", active: false },
];

const getDefaultIngredients = () =>
  Object.fromEntries(STUDIO_INGREDIENTS.map((i) => [i.id, i.default]));

const scrollToStudioSection = (id) => {
  const section = document.getElementById(id);
  if (!section) return;
  section.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function StudioPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, authHeaders, setCartCount, cartCount } = useAuth();

  const [base, setBase] = useState("mango");
  const [style, setStyle] = useState("andhra");
  const [spice, setSpice] = useState(50);
  const [salt, setSalt] = useState(50);
  const [oil, setOil] = useState(50);
  const [sourness, setSourness] = useState(50);
  const [texture, setTexture] = useState("chunky");
  const [ingredients, setIngredients] = useState(getDefaultIngredients);
  const [activeProfile, setActiveProfile] = useState(null);
  const [jarSize, setJarSize] = useState("regular");
  const [pickleName, setPickleName] = useState("");
  const [notes, setNotes] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    document.title = "Customization Studio | Swadishes";
    window.scrollTo(0, 0);
    const preBase = searchParams.get("base");
    const preStyle = searchParams.get("style");
    if (preBase && PICKLE_BASES.find((b) => b.id === preBase)) setBase(preBase);
    if (preStyle && REGIONAL_STYLES.find((s) => s.id === preStyle)) setStyle(preStyle);
    return () => { document.title = "Swadishes"; };
  }, [searchParams]);

  // After auth, run pending action
  useEffect(() => {
    if (isAuthenticated && pendingAction) {
      if (pendingAction === "save") doSave();
      else if (pendingAction === "cart") doAddToCart();
      setPendingAction(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, pendingAction]);

  const getPickleConfig = useCallback(() => ({
    base, style, spice, salt, oil, sourness, texture,
    ingredients: Object.entries(ingredients).filter(([, v]) => v).map(([k]) => k),
    profile: activeProfile?.id || null,
  }), [base, style, spice, salt, oil, sourness, texture, ingredients, activeProfile]);

  const applyProfile = useCallback((profile) => {
    setActiveProfile(profile);
    setSpice(profile.settings.spice);
    setSalt(profile.settings.salt);
    setOil(profile.settings.oil);
    setSourness(profile.settings.sourness || 40);
    const updated = { ...getDefaultIngredients() };
    profile.excludedIngredients.forEach((name) => {
      const key = name.replace(/\s+/g, "");
      const lcKey = key.charAt(0).toLowerCase() + key.slice(1);
      if (lcKey in updated) updated[lcKey] = false;
    });
    setIngredients(updated);
  }, []);

  const resetAll = useCallback(() => {
    setBase("mango"); setStyle("andhra");
    setSpice(50); setSalt(50); setOil(50); setSourness(50);
    setTexture("chunky"); setIngredients(getDefaultIngredients());
    setActiveProfile(null); setJarSize("regular");
    setPickleName(""); setNotes(""); setIsFavorite(false);
    setActiveStep(0);
  }, []);

  const applyPreset = useCallback((preset) => {
    setBase(preset.base); setStyle(preset.style);
    setSpice(preset.spice); setSalt(preset.salt);
    setOil(preset.oil); setSourness(preset.sourness);
    if (preset.texture) setTexture(preset.texture);
    setActiveProfile(null);
  }, []);

  const blendName = useMemo(() => {
    if (pickleName) return pickleName;
    const baseName = PICKLE_BASES.find((b) => b.id === base)?.name || "Custom";
    const parts = [];
    if (salt <= 25) parts.push("Low-Salt");
    if (spice <= 25) parts.push("Mild");
    else if (spice >= 75) parts.push("Fiery");
    if (oil <= 25) parts.push("Light");
    parts.push(baseName);
    parts.push("Pickle");
    return parts.join(" ");
  }, [pickleName, base, spice, salt, oil]);

  const recipeStory = useMemo(() => {
    const baseInfo = PICKLE_BASES.find((b) => b.id === base);
    const styleInfo = REGIONAL_STYLES.find((s) => s.id === style);
    const textureInfo = TEXTURES.find((t) => t.id === texture);
    const spiceDesc = spice >= 75 ? "bold and fiery" : spice >= 50 ? "well-spiced" : spice >= 25 ? "gently spiced" : "delicately mild";
    const oilDesc = oil >= 75 ? "richly marinated in oil" : oil >= 50 ? "with a balanced oil finish" : "lightly dressed";
    const sourDesc = sourness >= 60 ? "with a punchy tang" : sourness >= 30 ? "with a pleasant sourness" : "with subtle tartness";
    const activeIngs = Object.entries(ingredients).filter(([, v]) => v).map(([k]) => STUDIO_INGREDIENTS.find((i) => i.id === k)?.name).filter(Boolean);
    const ingText = activeIngs.length > 2 ? `Featuring ${activeIngs.slice(0, 2).join(", ")} and more.` : activeIngs.length > 0 ? `Made with ${activeIngs.join(" and ")}.` : "";
    return `A ${spiceDesc} ${baseInfo?.name.toLowerCase()} pickle in the ${styleInfo?.name.toLowerCase()} tradition, ${oilDesc}, ${sourDesc}. ${textureInfo?.name} cut for the perfect bite. ${ingText}`;
  }, [base, style, spice, oil, sourness, texture, ingredients]);

  const currentPrice = JAR_SIZES.find((j) => j.id === jarSize)?.price || 349;
  const baseInfo = useMemo(() => PICKLE_BASES.find((b) => b.id === base), [base]);
  const styleInfo = useMemo(() => REGIONAL_STYLES.find((s) => s.id === style), [style]);
  const jarInfo = useMemo(() => JAR_SIZES.find((j) => j.id === jarSize), [jarSize]);
  const activeIngredientCount = useMemo(
    () => Object.values(ingredients).filter(Boolean).length,
    [ingredients]
  );
  const mobileStep = STEP_ITEMS[activeStep];
  const progressRatio = ((activeStep + 1) / STEP_ITEMS.length) * 100;

  const goToStep = useCallback((index) => {
    setActiveStep(Math.max(0, Math.min(index, LAST_STEP_INDEX)));
  }, []);

  const goToNextStep = useCallback(() => {
    goToStep(activeStep + 1);
  }, [activeStep, goToStep]);

  const goToPrevStep = useCallback(() => {
    goToStep(activeStep - 1);
  }, [activeStep, goToStep]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!window.matchMedia("(max-width: 1023px)").matches) return undefined;

    const timeoutId = window.setTimeout(() => {
      scrollToStudioSection(STEP_ITEMS[activeStep]?.id);
    }, 40);

    return () => window.clearTimeout(timeoutId);
  }, [activeStep]);

  const doSave = async () => {
    try {
      const res = await fetch(`${API}/api/recipes`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          pickle_config: getPickleConfig(),
          pickle_name: blendName,
          notes,
          is_favorite: isFavorite,
        }),
      });
      if (res.ok) {
        setShowSaveToast(true);
        setTimeout(() => setShowSaveToast(false), 3000);
      }
    } catch {}
  };

  const doAddToCart = async () => {
    try {
      const res = await fetch(`${API}/api/cart/add`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          pickle_config: getPickleConfig(),
          jar_size: jarSize,
          pickle_name: blendName,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setCartCount(data.cart_count);
        setShowCartModal(true);
      }
    } catch {}
  };

  const handleSave = () => {
    if (!isAuthenticated) { setPendingAction("save"); setShowAuthModal(true); return; }
    doSave();
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) { setPendingAction("cart"); setShowAuthModal(true); return; }
    doAddToCart();
  };

  const state = {
    base, setBase, style, setStyle, spice, setSpice, salt, setSalt,
    oil, setOil, sourness, setSourness, texture, setTexture,
    ingredients, setIngredients, activeProfile, setActiveProfile,
    applyProfile, jarSize, setJarSize, pickleName, setPickleName,
    notes, setNotes, isFavorite, setIsFavorite, blendName, recipeStory,
    currentPrice, resetAll, applyPreset, handleSave,
    showCartModal, setShowCartModal, activeStep, setActiveStep,
    handleAddToCart,
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="studio-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-cream/90 backdrop-blur-xl border-b border-gray-100/50" data-testid="studio-header">
        <div className="section-container flex items-center justify-between h-14 lg:h-16">
          <button onClick={() => navigate("/")} data-testid="studio-back-btn" className="flex items-center gap-2 text-sw-text-secondary hover:text-earth-green transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm font-medium hidden sm:block">Home</span>
          </button>
          <div className="flex items-center gap-2">
            <img src={IMAGES.logo} alt="Swadishes" className="h-7 lg:h-9 rounded-md" />
            <span className="text-sm lg:text-base font-heading font-700 text-sw-text">Customization Studio</span>
          </div>
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <button onClick={() => navigate("/cart")} data-testid="studio-cart-btn" className="relative p-2 text-sw-text-muted hover:text-earth-green transition-colors">
                <ShoppingBag size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] bg-spice-terracotta text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            {!isAuthenticated && (
              <button onClick={() => setShowAuthModal(true)} data-testid="studio-login-btn" className="flex items-center gap-1 text-xs text-sw-text-muted hover:text-earth-green transition-colors">
                <User size={14} /> <span className="hidden sm:block">Sign In</span>
              </button>
            )}
            <button onClick={resetAll} data-testid="studio-reset-all" className="flex items-center gap-1 text-xs text-sw-text-muted hover:text-earth-green transition-colors">
              <RotateCcw size={14} /> <span className="hidden sm:block">Reset</span>
            </button>
          </div>
        </div>
        {/* Progress Bar */}
        <div className="section-container hidden pb-2 lg:block">
          <div className="flex gap-0.5">
            {STEPS.map((step, i) => (
              <div key={step} className="flex-1">
                <div className={`h-1 rounded-full transition-all duration-500 ${i <= activeStep ? "bg-earth-green" : "bg-cream-tertiary"}`} />
                <p className={`text-[9px] mt-0.5 text-center hidden lg:block transition-colors ${i <= activeStep ? "text-earth-green font-medium" : "text-sw-text-muted"}`}>{step}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-40 lg:pt-32 lg:pb-16">
        <div className="section-container">
          {/* Category Tabs */}
          <div className="mb-8 flex gap-2 overflow-x-auto pb-1 no-scrollbar sm:justify-center" data-testid="studio-category-tabs">
            {STUDIO_CATEGORIES.map((cat) => (
              <div key={cat.id} className="relative group">
                <button
                  data-testid={`studio-cat-${cat.id}`}
                  className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    cat.active
                      ? "bg-earth-green text-white shadow-md shadow-earth-green/20"
                      : "bg-white border border-gray-200 text-sw-text-muted cursor-default"
                  }`}
                >
                  {cat.label}
                  {!cat.active && <Lock size={10} className="inline ml-1.5 -mt-0.5" />}
                </button>
                {!cat.active && (
                  <div className="absolute -top-9 left-1/2 -translate-x-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    <span className="bg-sw-text text-white text-[11px] font-medium px-3 py-1.5 rounded-lg shadow-lg">
                      Coming Soon
                    </span>
                    <div className="w-2 h-2 bg-sw-text rotate-45 mx-auto -mt-1" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mb-6 space-y-4 lg:hidden">
            <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-[0_26px_80px_rgba(74,93,35,0.14)]">
              <img
                src={`${process.env.PUBLIC_URL}/kitchen.png`}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-center opacity-45"
              />
              <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(248,244,237,0.84)_0%,rgba(248,244,237,0.76)_45%,rgba(248,244,237,0.92)_100%)]" />
              <div className="relative space-y-4 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-earth-green">Live Studio</p>
                    <h1 className="mt-2 text-2xl font-heading font-800 leading-tight text-sw-text">{blendName}</h1>
                    <p className="mt-2 text-sm leading-6 text-sw-text-secondary line-clamp-2">{recipeStory}</p>
                  </div>
                  <button
                    onClick={() => setShowMobilePreview(true)}
                    className="shrink-0 rounded-full border border-earth-green/20 bg-white/80 px-3 py-2 text-xs font-medium text-earth-green shadow-sm"
                    data-testid="studio-open-mobile-preview"
                  >
                    <Eye size={14} className="inline mr-1 -mt-0.5" />
                    Preview
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {baseInfo && (
                    <span className="rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-medium text-sw-text shadow-sm">
                      Base: <span style={{ color: baseInfo.color }}>{baseInfo.name}</span>
                    </span>
                  )}
                  {styleInfo && (
                    <span className="rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-medium text-sw-text shadow-sm">
                      Style: {styleInfo.name}
                    </span>
                  )}
                  {activeProfile && (
                    <span className="rounded-full bg-earth-green/10 px-3 py-1.5 text-[11px] font-medium text-earth-green">
                      {activeProfile.name}
                    </span>
                  )}
                  <span className="rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-medium text-sw-text shadow-sm">
                    {jarInfo?.name || "Regular"} <span aria-hidden="true">&middot;</span> {jarInfo?.weight || "300g"}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Spice", value: spice, icon: Flame, color: "#9F3E18" },
                    { label: "Salt", value: salt, icon: Droplets, color: "#2E5C5A" },
                    { label: "Oil", value: oil, icon: Beef, color: "#D97706" },
                  ].map((item) => (
                    <div key={item.label} className="rounded-2xl bg-white/88 px-3 py-3 shadow-sm">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-sw-text-muted">
                        <item.icon size={12} style={{ color: item.color }} />
                        {item.label}
                      </div>
                      <div className="mt-2 text-lg font-heading font-700 text-sw-text">{item.value}%</div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between rounded-2xl bg-white/82 px-4 py-3 shadow-sm">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-sw-text-muted">Step</p>
                    <p className="mt-1 text-sm font-medium text-sw-text">{activeStep + 1} of {STEP_ITEMS.length}: {mobileStep?.label}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-sw-text-muted">Price</p>
                    <p className="mt-1 text-xl font-heading font-800 text-earth-green">&#8377;{currentPrice}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky top-14 z-20 -mx-1 space-y-3 bg-cream/95 px-1 py-2 backdrop-blur">
              <div className="rounded-2xl border border-gray-100 bg-white/90 p-3 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-sw-text-muted">Studio flow</p>
                    <p className="mt-1 text-sm font-medium text-sw-text">
                      Step {activeStep + 1} of {STEP_ITEMS.length}: {mobileStep?.label}
                    </p>
                  </div>
                  <span className="rounded-full bg-earth-green/10 px-3 py-1.5 text-[11px] font-semibold text-earth-green">
                    {Math.round(progressRatio)}%
                  </span>
                </div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-cream-secondary">
                  <div
                    className="h-full rounded-full bg-earth-green transition-all duration-300"
                    style={{ width: `${progressRatio}%` }}
                  />
                </div>
              </div>

              <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {STEP_ITEMS.map((step, index) => {
                  const isActive = index === activeStep;
                  const isComplete = index < activeStep;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => goToStep(index)}
                      className={`shrink-0 rounded-full px-4 py-2.5 text-xs font-medium transition-all ${
                        isActive
                          ? "bg-earth-green text-white shadow-md shadow-earth-green/20"
                          : isComplete
                            ? "bg-earth-green/10 text-earth-green"
                            : "bg-white text-sw-text-muted border border-gray-200"
                      }`}
                      data-testid={`studio-mobile-step-${step.id}`}
                    >
                      {index + 1}. {step.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowMobilePreview(true)}
                className="rounded-2xl border border-earth-green/20 bg-white px-4 py-3 text-left shadow-sm"
              >
                <div className="text-[11px] uppercase tracking-[0.18em] text-sw-text-muted">Preview</div>
                <div className="mt-1 text-sm font-medium text-sw-text">{activeIngredientCount} ingredients active</div>
              </button>
              <button
                type="button"
                onClick={resetAll}
                className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left shadow-sm"
              >
                <div className="text-[11px] uppercase tracking-[0.18em] text-sw-text-muted">Reset</div>
                <div className="mt-1 text-sm font-medium text-sw-text">Start over clean</div>
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-6 lg:gap-10">
            <div className="lg:col-span-3 order-1 lg:order-1">
              <StudioBuilder
                {...state}
                mobileActiveStep={activeStep}
                onNextStep={goToNextStep}
                onPrevStep={goToPrevStep}
                isFirstStep={activeStep === 0}
                isLastStep={activeStep === LAST_STEP_INDEX}
              />
            </div>
            <div className="hidden lg:block lg:col-span-2 lg:order-2">
              <StudioPreview {...state} />
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Sticky Bottom Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 border-t border-gray-100 bg-white/95 p-3 backdrop-blur-xl lg:hidden ${
          showMobilePreview ? "hidden" : ""
        }`}
        data-testid="studio-mobile-bar"
      >
        <div className="space-y-3">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.18em] text-sw-text-muted">
                Step {activeStep + 1} of {STEP_ITEMS.length}
              </p>
              <p className="mt-1 truncate text-sm font-heading font-700 text-sw-text">{mobileStep?.label}</p>
              <p className="truncate text-xs text-sw-text-muted">{blendName}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-[0.18em] text-sw-text-muted">Price</p>
              <p className="mt-1 text-lg font-heading font-800 text-earth-green">&#8377;{currentPrice}</p>
            </div>
          </div>

          <div className={`grid gap-2 ${activeStep === 0 ? "grid-cols-2" : "grid-cols-3"}`}>
            {activeStep > 0 && (
              <button
                type="button"
                onClick={goToPrevStep}
                className="rounded-full border border-gray-200 px-3 py-3 text-xs font-medium text-sw-text"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowMobilePreview(true)}
              className="rounded-full border border-earth-green/20 px-3 py-3 text-xs font-medium text-earth-green"
            >
              Preview
            </button>
            {activeStep === LAST_STEP_INDEX ? (
              <button
                type="button"
                onClick={handleAddToCart}
                data-testid="mobile-add-cart"
                className="rounded-full bg-earth-green px-3 py-3 text-xs font-medium text-white shadow-lg shadow-earth-green/20"
              >
                Add to Cart
              </button>
            ) : (
              <button
                type="button"
                onClick={goToNextStep}
                className="rounded-full bg-earth-green px-3 py-3 text-xs font-medium text-white shadow-lg shadow-earth-green/20"
              >
                Next Step
              </button>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showMobilePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={() => setShowMobilePreview(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-[2rem] bg-cream px-4 pb-32 pt-4"
              onClick={(e) => e.stopPropagation()}
              data-testid="studio-mobile-preview-drawer"
            >
              <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-gray-300" />
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-earth-green">Live Preview</p>
                  <h2 className="mt-1 text-xl font-heading font-800 text-sw-text">{blendName}</h2>
                  <p className="mt-1 text-sm text-sw-text-muted">
                    {jarInfo?.name || "Regular"} <span aria-hidden="true">&middot;</span> {jarInfo?.weight || "300g"}{" "}
                    <span aria-hidden="true">&middot;</span> &#8377;{currentPrice}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMobilePreview(false)}
                  className="rounded-full border border-gray-200 p-2 text-sw-text"
                  data-testid="studio-close-mobile-preview"
                >
                  <X size={18} />
                </button>
              </div>
              <StudioPreview {...state} mobileDrawer />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart Added Modal */}
      <AnimatePresence>
        {showCartModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCartModal(false)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()} data-testid="cart-added-modal" className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center">
              <div className="w-16 h-16 rounded-full bg-earth-green/10 flex items-center justify-center mx-auto mb-4">
                <ShoppingBag size={28} className="text-earth-green" />
              </div>
              <h3 className="text-xl font-heading font-700 text-sw-text mb-2">Added to Cart!</h3>
              <div className="bg-cream-secondary rounded-2xl p-4 mb-6 text-left">
                <p className="text-base font-heading font-700 text-sw-text">{blendName}</p>
                <p className="text-xs text-sw-text-muted mt-1">{recipeStory.slice(0, 120)}...</p>
                <p className="text-sm font-heading font-700 text-earth-green mt-2">&#8377;{currentPrice} &middot; {JAR_SIZES.find((j) => j.id === jarSize)?.weight}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button onClick={() => setShowCartModal(false)} data-testid="cart-modal-continue"
                  className="flex-1 border border-earth-green text-earth-green rounded-full py-3 font-medium text-sm hover:bg-earth-green hover:text-white transition-all">
                  Continue
                </button>
                <button onClick={() => navigate("/cart")} data-testid="cart-modal-go-to-cart"
                  className="flex-1 bg-earth-green text-white rounded-full py-3 font-medium text-sm hover:bg-earth-green-dark transition-all shadow-lg shadow-earth-green/20">
                  View Cart
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Toast */}
      <AnimatePresence>
        {showSaveToast && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} className="fixed bottom-20 lg:bottom-8 left-1/2 -translate-x-1/2 z-[100] bg-earth-green text-white rounded-full px-6 py-3 shadow-lg flex items-center gap-2" data-testid="save-toast">
            <Check size={18} />
            <span className="text-sm font-medium">Recipe saved to My Kitchen!</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
